import logoService from '../services/logoService';

export const getAdvisorInitials = (advisorName) => {
  if (!advisorName) return '?';
  
  const words = advisorName.trim().split(/\s+/);
  
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  } else {
    const firstInitial = words[0].charAt(0).toUpperCase();
    const lastInitial = words[words.length - 1].charAt(0).toUpperCase();
    return firstInitial + lastInitial;
  }
};

export const getAdvisorColor = (advisorName) => {
  if (!advisorName) return '#4caf50';
  
  let hash = 0;
  const str = advisorName.toLowerCase().trim();
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  hash = Math.abs(hash);
  hash = hash * 2654435761 % Math.pow(2, 32);
  
  const colorPalette = [
    '#0E7A62',
    '#d32f2f',
    '#388e3c',
    '#ff9800',
    '#9c27b0',
    '#00796b',
    '#795548',
    '#e91e63',
    '#3f51b5',
    '#607d8b',
    '#4caf50',
    '#ff5722',
    '#673ab7',
    '#009688',
    '#ffc107',
    '#8bc34a',
    '#f44336',
    '#2196f3',
    '#cddc39',
    '#ff6f00'
  ];
  
  const colorIndex = hash % colorPalette.length;
  return colorPalette[colorIndex];
};

// Industry the classifier could not determine, as stored by the API
const UNCLASSIFIED_INDUSTRY = 'UNKNOWN';

// Display name for an industry value from the API
export const industryLabel = (name) => (name === UNCLASSIFIED_INDUSTRY ? 'Unclassified' : name);

export const peopleLabel = (count) => `${count} ${count === 1 ? 'person' : 'people'}`;

// Advisors connected to a contact: every advisor when the API sent known_by, else the row's own
export const contactAdvisors = (contact) => {
  // The API's known_by is authoritative, including when no advisor knows the person
  if (Array.isArray(contact?.known_by)) return contact.known_by;
  const advisor = contact?.advisor_name || contact?.advisor || contact?.contact_source || contact?.contact;
  return advisor ? [advisor] : [];
};

/**
 * Overview graph: hubs (industries, or ICP target lists), their companies, a people-count
 * node per company with people and, when a hub has more companies than shown, a "+N more"
 * node. Target accounts with nobody on file yet still appear, without a count node.
 */
export const buildHierarchicalGraphData = (hierarchicalData) => {
  if (!hierarchicalData?.data) {
    return { nodes: [], links: [] };
  }

  const nodes = [];
  const links = [];
  const companyIds = new Set();

  hierarchicalData.data.forEach(hub => {
    if (!hub || !hub.name) return;

    const isIcp = hub.type === 'icp';
    const hubId = `${isIcp ? 'icp' : 'industry'}-${hub.name}`;
    nodes.push({
      id: hubId,
      name: isIcp ? `${hub.metadata?.rank ? `${hub.metadata.rank}. ` : ''}${hub.name}` : industryLabel(hub.name),
      group: 'industry',
      type: isIcp ? 'icp' : 'industry',
      industryData: {
        name: hub.name,
        id: hub.id,
        metadata: hub.metadata
      },
      size: 20,
      level: 0
    });

    (hub.children || []).forEach(company => {
      if (!company || !company.name) return;
      const contactCount = company.metadata?.contact_count || 0;
      if (!contactCount && !company.metadata?.target_account) return;

      // A company on two hubs is drawn once per hub
      const companyId = `company-${hubId}-${company.id}`;
      if (!companyIds.has(companyId)) {
        companyIds.add(companyId);
        nodes.push({
          id: companyId,
          name: company.name,
          group: 'company',
          type: 'company',
          companyData: {
            name: company.name,
            company_name: company.metadata.company_name || company.name,
            id: company.id,
            industry: company.metadata.industry || hub.name,
            logo_uuid: company.metadata.logo_uuid,
            logo_url: company.metadata.logo_url,
            sector: company.metadata.sector,
            contact_count: contactCount,
            network_people: company.metadata.network_people || 0,
            relationship_managers: company.metadata.relationship_managers || [],
            target_account: company.metadata.target_account || null
          },
          size: 15,
          level: 1
        });
        links.push({ source: hubId, target: companyId, type: 'industry_has_company', value: 2 });
      }

      if (contactCount > 0) {
        const contactCountId = `contact-count-${hubId}-${company.id}`;
        nodes.push({
          id: contactCountId,
          name: peopleLabel(contactCount),
          group: 'contact_count',
          type: 'contact_count',
          contactCountData: {
            count: contactCount,
            company: company,
            industry: hub
          },
          size: Math.max(8, Math.min(18, Math.log(contactCount) * 3)),
          level: 2,
          clickable: true
        });
        links.push({ source: companyId, target: contactCountId, type: 'company_has_contacts', value: 1 });
      }
    });

    const hidden = hub.metadata?.hidden_company_count || 0;
    if (hidden > 0) {
      const moreId = `more-${hubId}`;
      nodes.push({
        id: moreId,
        name: `${hidden} more ${isIcp ? 'accounts' : 'companies'}`,
        group: 'more_companies',
        type: 'more_companies',
        industryName: hub.name,
        hiddenCount: hidden,
        size: 10,
        level: 1,
        clickable: true
      });
      links.push({ source: hubId, target: moreId, type: 'industry_has_more', value: 1 });
    }
  });

  return { nodes, links };
};

// Relationship managers as a phrase: "Mukundhan", "Gowri and Mukundhan", "A, B and C"
export const managerNames = (managers = []) => {
  const names = managers.map(manager => manager.name);
  if (names.length < 2) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

// One-line summary of a company's place on a target list, e.g. "Tier A · 9/10 · #1 · managed by Mukundhan".
// withManagers: false leaves out who manages the company, for views that show it separately.
export const targetAccountLabel = (account, { withManagers = true } = {}) => {
  if (!account) return '';
  const score = account.score != null ? `${account.score}/${account.score_max}` : null;
  // Who manages the company now; the workbook's Owner cell when the response doesn't say
  const managers = Array.isArray(account.relationship_managers)
    ? managerNames(account.relationship_managers)
    : account.owner;
  return [
    account.tier ? `Tier ${account.tier}` : account.likelihood,
    score,
    account.list_rank != null ? `#${account.list_rank}` : null,
    withManagers && managers ? `managed by ${managers}` : null
  ].filter(Boolean).join(' · ');
};

export const OUTREACH_STAGE_LABELS = {
  not_contacted: 'Not contacted yet',
  contacted: 'Contacted, no reply yet',
  declined: 'Not interested',
  met: 'Met',
  in_progress: 'In progress'
};

/**
 * Drill-down graph: one company node per company and one node per person, linked to the
 * company they work at. `sources` on each person lists the advisors who know them.
 */
export const buildGraphData = (contacts = []) => {
  if (!Array.isArray(contacts) || contacts.length === 0) {
    return { nodes: [], links: [] };
  }

  const nodes = [];
  const links = [];
  const companyNodes = new Map();
  const contactIds = new Set();

  contacts.forEach(contact => {
    if (!contact || typeof contact !== 'object' || !contact.id) return;

    const companyDetail = contact.company_detail || (typeof contact.company === 'object' ? contact.company : null);
    const companyName = (contact.company_name || companyDetail?.name || '').trim();

    if (companyName && !companyNodes.has(companyName)) {
      const logoUuid = companyDetail?.logo_uuid || contact.logo_uuid;
      const companyNode = {
        id: `company-${companyName}`,
        name: companyName,
        group: 'company',
        companyData: {
          name: companyName,
          logoUrl: logoService.getLogoUrl({ logo_uuid: logoUuid, logo_url: contact.company_logo_url, name: companyName }),
          logo_uuid: logoUuid,
          sector: companyDetail?.sector || contact.sector || null,
          industry: companyDetail?.industry || contact.industry || null,
          id: companyDetail?.id || null
        }
      };
      companyNodes.set(companyName, companyNode);
      nodes.push(companyNode);
    }

    const contactId = `contact-${contact.id}`;
    if (contactIds.has(contactId)) return;
    contactIds.add(contactId);

    const advisors = contactAdvisors(contact);
    nodes.push({
      id: contactId,
      group: 'contact',
      contactData: {
        ...contact,
        sourceCount: advisors.length,
        sources: advisors
      }
    });

    if (companyName) {
      links.push({ source: contactId, target: `company-${companyName}`, type: 'works_at' });
    }
  });

  return { nodes, links };
};

export const getNodeColor = (node, theme = 'light') => {
  const colors = {
    light: {
      contact: '#e35876ff',
      company: '#efcc62ff',
      position: '#26a260ff',
      industry: '#0E7A62',
      contact_count: '#ff9800'
    },
    dark: {
      contact: '#5aa3f0',
      company: '#66d9aa',
      position: '#f28b82',
      industry: '#42a5f5',
      contact_count: '#ffb74d'
    }
  };
  
  if (node.group === 'contact' && node.contactData) {
    const advisor = contactAdvisors(node.contactData)[0];
    if (advisor) {
      return getAdvisorColor(advisor);
    }
  }
  
  return colors[theme][node.group] || colors[theme].contact;
};

export const formatContactName = (contact) => {
  if (!contact) return 'Unknown Contact';
  
  const firstName = contact.first_name || '';
  const lastName = contact.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();
  
  return fullName || `Contact ${contact.id}`;
};

export const formatCompanyDisplay = (companyData) => {
  if (!companyData) return 'Unknown Company';
  
  return companyData.name || 'Unknown Company';
};
